import { useState, useEffect, useCallback } from "react";
import { Plus, GripVertical, Edit2, Trash2, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { crmApi } from "@/utils/api";

const COLORS = ["#6B7280", "#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6"];

const StagesPage = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", color: "" });
  const [showNewForm, setShowNewForm] = useState(false);
  const [newStage, setNewStage] = useState({ name: "", color: COLORS[0] });

  const fetchStages = useCallback(async () => {
    try {
      const res = await crmApi.getStages();
      setStages(res.data);
    } catch (error) { /* Error handled silently */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStages(); }, [fetchStages]);

  const handleCreate = async () => {
    if (!newStage.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      await crmApi.createStage({ id: crypto.randomUUID(), name: newStage.name, color: newStage.color, order: stages.length });
      toast.success("Etapa creada");
      setShowNewForm(false);
      setNewStage({ name: "", color: COLORS[0] });
      fetchStages();
    } catch (error) { toast.error("Error al crear"); }
  };

  const handleUpdate = async (id) => {
    if (!editForm.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      await crmApi.updateStage(id, editForm);
      toast.success("Actualizada");
      setEditingId(null);
      fetchStages();
    } catch (error) { toast.error("Error al actualizar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta etapa?")) return;
    try {
      await crmApi.deleteStage(id);
      toast.success("Eliminada");
      fetchStages();
    } catch (error) { toast.error("Error al eliminar"); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="stages-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">Etapas del Pipeline</h1>
            <p className="text-[#A3A3A3] text-sm">Configura las etapas de seguimiento</p>
          </div>
          <Button onClick={() => setShowNewForm(true)} className="bg-[#D4AF37] text-black hover:bg-[#FFE082]" data-testid="add-stage-btn">
            <Plus size={18} className="mr-2" />Nueva Etapa
          </Button>
        </div>

        {showNewForm && (
          <Card className="bg-[#121212] border-[#D4AF37]/50">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label className="text-white">Nombre</Label>
                  <Input value={newStage.name} onChange={(e) => setNewStage(p => ({...p,name:e.target.value}))}
                    className="bg-[#1a1a1a] border-white/10 text-white" placeholder="Ej: En Revisión" autoFocus />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Color</Label>
                  <div className="flex gap-2">
                    {COLORS.map(c => (<button key={c} type="button" onClick={() => setNewStage(p => ({...p,color:c}))}
                      className={`w-8 h-8 rounded-full ${newStage.color === c ? "ring-2 ring-white" : ""}`} style={{backgroundColor:c}} />))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCreate} className="bg-[#D4AF37] text-black hover:bg-[#FFE082]"><Check size={18} /></Button>
                  <Button variant="ghost" onClick={() => setShowNewForm(false)} className="text-[#A3A3A3] hover:text-white"><X size={18} /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-[#121212] border-white/5">
          <CardHeader><CardTitle className="text-white text-lg">Pipeline de Ventas</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div></div>
            ) : stages.length === 0 ? (
              <p className="text-[#A3A3A3] text-center py-8">No hay etapas. Agrega la primera.</p>
            ) : (
              <div className="space-y-2">
                {stages.map((stage, idx) => (
                  <div key={stage.id} className="flex items-center gap-4 p-3 rounded-lg bg-[#1a1a1a] hover:bg-[#222]">
                    <GripVertical size={18} className="text-[#A3A3A3]" />
                    <div className="w-4 h-4 rounded-full" style={{backgroundColor:stage.color}} />
                    {editingId === stage.id ? (
                      <div className="flex-1 flex flex-col sm:flex-row gap-3 items-center">
                        <Input value={editForm.name} onChange={(e) => setEditForm(p => ({...p,name:e.target.value}))}
                          className="bg-[#121212] border-white/10 text-white flex-1" autoFocus />
                        <div className="flex gap-1">
                          {COLORS.map(c => (<button key={c} type="button" onClick={() => setEditForm(p => ({...p,color:c}))}
                            className={`w-6 h-6 rounded-full ${editForm.color === c ? "ring-2 ring-white" : ""}`} style={{backgroundColor:c}} />))}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => handleUpdate(stage.id)} className="bg-[#D4AF37] text-black hover:bg-[#FFE082]"><Check size={16} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="text-[#A3A3A3] hover:text-white"><X size={16} /></Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="flex-1 text-white">{stage.name}</span>
                        <span className="text-[#A3A3A3] text-sm">#{idx + 1}</span>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => { setEditingId(stage.id); setEditForm({name:stage.name,color:stage.color}); }}
                            className="text-[#A3A3A3] hover:text-white h-8 w-8"><Edit2 size={14} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDelete(stage.id)}
                            className="text-[#A3A3A3] hover:text-red-500 h-8 w-8"><Trash2 size={14} /></Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default StagesPage;
